/*
 * This file contains common URL transformations that are required for i.e. links to different parts of the site.
 */


import _ from 'lodash';
import {DEFAULT_LANGUAGE, getLanguage} from './language.utils';
import {logger} from './log.helper';

/**
 * Create a URL by joining parts of paths together.
 *
 * All parts will be trimmed of surrounding /, concatenated with / in between and returned with surrounding /.
 *
 * @return {string}
 */
function joinURL(...parts) {
  parts = parts.map(part => _.trim(String(part), '/'));
  return `/${parts.join('/')}/`;
}


/**
 * Build a query string out of the given params.
 *
 * This is reimplemented because we cannot use Angular's $httpParamSerializer in non-Angular code.
 *
 * @param {Array.<{key: string, value: string}>} params List of parameters to add to the query string. The same
 *   parameter can be added many times.
 */
function buildQuery(params) {
  const parts = params.reduce((acc, {key, value}) => {
    acc.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    return acc;
  }, []);

  return parts.join('&');
}


/**
 * Build a query string out of a param dict.
 *
 * Helper for the function above using an object instead of a list. The same parameter cannot be added many times.
 *
 * @param {Object.<string, string>} params
 */
function buildQueryFromDict(params) {
  const params_list = Object.keys(params).reduce((acc, param) => {
    acc.push({key: param, value: params[param]});
    return acc;
  }, []);

  return buildQuery(params_list);
}


/**
 * ID transform URL that is used by WP to convert our IDs into proper WP URLs.
 * @type {string}
 */
const ID_TRANSFORM = 'oo';

const NORMAL_TRANSFORM = 'op';

const TAXONOMY_TRANSFORM = 'ot';

/**
 * Allowed tumbnails sizes in WP.
 * @type {Array.<String>}
 */
const ALLOWED_IMAGES_SIZES = ['thumbnail', 'medium', 'large', 'width-grid-half'];

/**
 * Default image size to use in filters if size is not specified.
 * @type {String}
 */
const DEFAULT_IMAGE_SIZE = ALLOWED_IMAGES_SIZES[0];

/**
 * Return URL that uses ID_TRANSFORM in the beginning.
 */
function getTransformedIdURL(...rest) {
  return joinURL(ID_TRANSFORM, getLanguage(), ...rest);
}

/**
 * Return url that use NORMAL_TRANSFORM in the beginning.
 */
function getTransformedURL(...rest) {
  return joinURL(NORMAL_TRANSFORM, getLanguage(), ...rest);
}

/**
 * Return URL that uses TAXONOMY_TRANSFORM in the beginning.
 */
function getTransformedTaxonomyURL(...rest) {
  return joinURL(TAXONOMY_TRANSFORM, getLanguage(), ...rest);
}

function getThumb(base_url, size) {
  if (!ALLOWED_IMAGES_SIZES.includes(size)) {
    logger().error(`Tried to fetch unsupported image size: ${size}`);
    return '';
  }

  return joinURL(base_url, 'image', size);
}


/*
 * HOME PAGE
 */


/**
 * Path of the home page in WP.
 * @type {string}
 */
const HOME_PAGE = '/';

function homePageURL() {
  // Home page is a special case
  const lang = getLanguage();
  if (lang === DEFAULT_LANGUAGE) {
    return HOME_PAGE;
  }
  else {
    return joinURL(lang, HOME_PAGE);
  }
}


/*
 * SERVICE PAGE
 */

/**
 * Get URL of service's page.
 * @param {Service} service
 * @return {string}
 */
function serviceURL(service) {
  return getTransformedIdURL('service', service.id);
}


/*
 * PERSON PAGE
 */


/**
 * Get URL of person's page.
 * @param {Person} person
 * @return {string}
 */
function personURL(person) {
  return getTransformedIdURL('person', person.id);
}


/*
 * LOCATION PAGE
 */


/**
 * Get URL of location's page.
 * @param {Location} location
 * @return {string}
 */
function locationURL(location) {
  return getTransformedIdURL('location', location.id);
}

/**
 * Get URL to thumbnail of location.
 * @param {Location} location
 * @param {string} size
 * @return {string}
 */
function locationImageURL(location, size=DEFAULT_IMAGE_SIZE) {
  return getThumb(locationURL(location), size);
}


/**
 * Get URL to image of service.
 * @param {Service} service
 * @param {string} size
 * @return {string}
 */
function serviceImageURL(service, size='width-grid-half') {
  return getThumb(serviceURL(service), size);
}

/**
 * Get URL of city's page.
 * @param {City} city
 * @return {string}
 */
function cityURL(city) {
  return getTransformedTaxonomyURL('city', city.id);
}

/**
 * Base url for Google Maps search
 * @type {string}
 */
const GOOGLE_MAPS_SEARCH_BASE_URL = 'https://www.google.com/maps/search/?api=1&query=';

/**
 * First keywords for Google Maps search before any other keywords
 * @type {string[]}
 */
const GOOGLE_MAPS_CITY_SEARCH_PREFIX_PARTS = [
  'Oral',
  'Hammaslääkärit',
]

/**
 * Character to use to join keywords when constructing Google Maps search url
 * @type {string}
 */
const GOOGLE_MAPS_CITY_SEARCH_PARTS_DELIMITTER = '+';

/**
 * Function to generate Google Maps search url
 * @param {string|string[]} keywords
 * @return {string}
 */
function googleMapsSearchUrl(keywords) {
  const keywordParts = Array.isArray(keywords) ? keywords : keywords.split(' ');
  let url = `${GOOGLE_MAPS_SEARCH_BASE_URL}${GOOGLE_MAPS_CITY_SEARCH_PREFIX_PARTS.join(GOOGLE_MAPS_CITY_SEARCH_PARTS_DELIMITTER)}`;
  url = `${url}${GOOGLE_MAPS_CITY_SEARCH_PARTS_DELIMITTER}`;
  url = `${url}${keywordParts.join(GOOGLE_MAPS_CITY_SEARCH_PARTS_DELIMITTER)}`;
  return url;
}

const mod = angular.module('app.commonUrlFilters', [])

.filter('locationImage', () => {
  return (location, size=DEFAULT_IMAGE_SIZE) => locationImageURL(location, size);
})

.filter('personUrl', () => {
  return person => person.url.length > 0 ? person.url : personURL(person);
})

.filter('locationUrl', () => {
  return location => location.url.length > 0 ? location.url : locationURL(location);
})

;

export {
  serviceURL,
  serviceImageURL,
  personURL,
  locationURL,
  locationImageURL,
  cityURL,
  homePageURL,
  buildQueryFromDict,
  getTransformedURL,
  joinURL,
  googleMapsSearchUrl,
};
export default mod.name;
